import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  TablePagination,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import { Add, ContentCopy, Delete, Edit, Email, OpenInNew, Visibility, VpnKey } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import type { CapitalContribution, CapitalManagementSummary, CapitalPartner } from '../../../models/StockPickTypes';
import {
  addCapitalContribution,
  createCapitalPartner,
  deleteCapitalContribution,
  getCapitalPartner,
  getCapitalPartnersPage,
  getCapitalSummary,
  updateCapitalPartner,
  generatePartnerPortalPassword,
  sendPartnerStatementEmail,
  type GeneratePartnerPortalPasswordResult,
} from '../../../services/request/CapitalManagement';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { formatDateForPdf } from '../../../utils/pdfFormat';
import { PaginationConst } from '../../../services/constants/AppConstant';
import PartnerCapitalFlowGuide from './PartnerCapitalFlowGuide';
import PartnerLendingVisibilityPanel from './PartnerLendingVisibilityPanel';
import { getPartnerControlSnapshot } from '../../../services/request/CapitalAnalytics';
import type { PartnerControlSnapshot } from '../../../models/CapitalAnalyticsTypes';

const STOCK_PICKS_PATH = '/admin/capitals/stock-picks';

const emptyPartner = (): CapitalPartner => ({ name: '', status: 'ACTIVE' });

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CapitalPartnersHub: React.FC = () => {
  const [summary, setSummary] = useState<CapitalManagementSummary | null>(null);
  const [rows, setRows] = useState<CapitalPartner[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CapitalPartner>(emptyPartner());
  const [saving, setSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<CapitalPartner | null>(null);
  const [contribAmount, setContribAmount] = useState('');
  const [contribDate, setContribDate] = useState(new Date().toISOString().slice(0, 10));
  const [contribNote, setContribNote] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<GeneratePartnerPortalPasswordResult | null>(null);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [detailTab, setDetailTab] = useState(0);
  const [lendingSnapshot, setLendingSnapshot] = useState<PartnerControlSnapshot | null>(null);

  const [statementOpen, setStatementOpen] = useState(false);
  const [statementPartner, setStatementPartner] = useState<CapitalPartner | null>(null);
  const [statementRecipients, setStatementRecipients] = useState('');
  const [statementSubject, setStatementSubject] = useState('');
  const [statementNote, setStatementNote] = useState('');
  const [sendingStatement, setSendingStatement] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, list] = await Promise.all([
        getCapitalSummary(),
        getCapitalPartnersPage(page, PaginationConst.PAGE_SIZE),
      ]);
      setSummary(sum);
      setRows(list.result ?? []);
      setTotal(list.totalCount ?? 0);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load capital partners');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyPartner());
    setFormOpen(true);
  };

  const openEdit = (partner: CapitalPartner) => {
    setForm({ ...partner });
    setFormOpen(true);
  };

  const openDetail = async (id: string) => {
    try {
      const p = await getCapitalPartner(id);
      setDetail(p);
      setDetailTab(0);
      setLendingSnapshot(null);
      setDetailOpen(true);
      getPartnerControlSnapshot(p.id ?? id).then(setLendingSnapshot);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load partner');
    }
  };

  const handleSavePartner = async () => {
    if (!form.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await updateCapitalPartner(form.id, form);
        toast.success('Partner updated');
      } else {
        await createCapitalPartner(form);
        toast.success('Partner created');
      }
      setFormOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddContribution = async () => {
    if (!detail?.id || !contribAmount) return;
    try {
      await addCapitalContribution(detail.id, {
        amount: Number(contribAmount),
        contributionDate: contribDate,
        referenceNote: contribNote || undefined,
      });
      toast.success('Capital recorded');
      setContribAmount('');
      setContribNote('');
      const refreshed = await getCapitalPartner(detail.id);
      setDetail(refreshed);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed');
    }
  };

  const handleGeneratePortalPassword = async () => {
    if (!detail?.id) return;
    setGeneratingPassword(true);
    try {
      const result = await generatePartnerPortalPassword(detail.id);
      setGeneratedPassword(result);
      setPasswordDialogOpen(true);
      const refreshed = await getCapitalPartner(detail.id);
      setDetail(refreshed);
      load();
      toast.success('Portal password generated — share it securely with the partner');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to generate password');
    } finally {
      setGeneratingPassword(false);
    }
  };

  const handleCopyPassword = () => {
    if (!generatedPassword?.plainPassword) return;
    navigator.clipboard.writeText(generatedPassword.plainPassword);
    toast.success('Password copied');
  };

  const handleTogglePortalAccess = async (enabled: boolean) => {
    if (!detail?.id) return;
    try {
      const updated = await updateCapitalPartner(detail.id, {
        ...detail,
        portalAccessEnabled: enabled,
      });
      setDetail(updated);
      load();
      toast.success(enabled ? 'Portal access enabled' : 'Portal access disabled');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const openStatementDialog = async (partner: CapitalPartner) => {
    if (!partner.id) return;
    setStatementSubject('');
    setStatementNote('');
    setStatementRecipients(partner.email?.trim() ?? '');
    setStatementPartner(partner);
    setStatementOpen(true);
    // Refresh detail in background so the preview reflects latest totals
    try {
      const fresh = await getCapitalPartner(partner.id);
      setStatementPartner((prev) => (prev && prev.id === fresh.id ? { ...prev, ...fresh } : prev));
    } catch {
      // Non-fatal; the partner row already has summary numbers
    }
  };

  const parseRecipientEmails = (raw: string): string[] => {
    return raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  const handleSendStatement = async () => {
    if (!statementPartner?.id) return;
    const recipients = parseRecipientEmails(statementRecipients);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient email');
      return;
    }
    const invalid = recipients.find((r) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r));
    if (invalid) {
      toast.error(`Invalid email address: ${invalid}`);
      return;
    }
    setSendingStatement(true);
    try {
      const res = await sendPartnerStatementEmail(statementPartner.id, {
        recipientEmails: recipients,
        subject: statementSubject.trim() || undefined,
        note: statementNote.trim() || undefined,
      });
      toast.success(`Statement sent to ${res.emailsSent} recipient${res.emailsSent === 1 ? '' : 's'}`);
      setStatementOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to send statement');
    } finally {
      setSendingStatement(false);
    }
  };

  const handleDeleteContribution = async (contributionId: string) => {
    if (!detail?.id) return;
    if (!window.confirm('Remove this capital contribution?')) return;
    try {
      await deleteCapitalContribution(detail.id, contributionId);
      const refreshed = await getCapitalPartner(detail.id);
      setDetail(refreshed);
      load();
      toast.success('Contribution removed');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed');
    }
  };

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Capital Partners</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Investment capital from partners — separate from lending deposits and borrower FDs.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add partner</Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This module tracks <strong>capital partner funding</strong> for stock plans and lending deployment.
        It is separate from Lending → Deposits (borrower FDs).
      </Alert>

      <PartnerCapitalFlowGuide />

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">Total contributed</Typography>
              <Typography variant="h5">{formatCurrency(summary.totalContributed)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">Deployed on plans</Typography>
              <Typography variant="h5">{formatCurrency(summary.totalAllocatedOnPlans)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">Available to allocate</Typography>
              <Typography variant="h5" color="success.main">{formatCurrency(summary.totalAvailable)}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Contributed</TableCell>
                  <TableCell align="right">On plans</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Portal</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                      {row.email && <Typography variant="caption" display="block">{row.email}</Typography>}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.totalContributed ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalAllocated ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.availableBalance ?? 0)}</TableCell>
                    <TableCell><Chip size="small" label={row.status ?? 'ACTIVE'} /></TableCell>
                    <TableCell>
                      {row.portalAccessEnabled ? (
                        <Chip size="small" color="success" label={row.hasPortalPassword ? 'Active' : 'No pwd'} variant="outlined" />
                      ) : (
                        <Chip size="small" label="Off" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Capture capital & view ledger"><IconButton size="small" onClick={() => row.id && openDetail(row.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Email wealth statement">
                        <span>
                          <IconButton size="small" onClick={() => openStatementDialog(row)}>
                            <Email fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={PaginationConst.PAGE_SIZE} rowsPerPageOptions={[PaginationConst.PAGE_SIZE]} />
        </CardContent>
      </Card>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit partner' : 'New capital partner'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
            <TextField label="Email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
            <TextField
              label="Phone (portal login)"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              helperText="10-digit mobile — used for partner portal sign-in"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.portalAccessEnabled ?? false}
                  onChange={(e) => setForm({ ...form, portalAccessEnabled: e.target.checked })}
                />
              }
              label="Enable partner portal access"
            />
            <TextField label="Notes" value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline minRows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePartner} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            {detail?.name} — Partner capital
            {detail?.email && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {detail.email}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Email />}
            onClick={() => detail && openStatementDialog(detail)}
          >
            Email statement
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <PartnerCapitalFlowGuide variant="compact" />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Contributed ${formatCurrency(detail.totalContributed ?? 0)}`} />
                <Chip label={`On plans ${formatCurrency(detail.totalAllocated ?? 0)}`} variant="outlined" />
                {lendingSnapshot && lendingSnapshot.lendingInvested > 0 && (
                  <Chip
                    label={`Lending ${formatCurrency(lendingSnapshot.lendingInvested)}`}
                    color="warning"
                    variant="outlined"
                    onClick={() => setDetailTab(2)}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
                <Chip label={`Available ${formatCurrency(detail.availableBalance ?? 0)}`} color="success" variant="outlined" />
              </Stack>

              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} variant="scrollable" scrollButtons="auto">
                <Tab label="Capture capital" />
                <Tab label="On stock plans" />
                <Tab label="Lending" />
                <Tab label="Partner portal" />
              </Tabs>

              <TabPanel value={detailTab} index={0}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Record money the partner has transferred into the fund. This is the only place to capture new capital.
                </Typography>
                <Typography variant="subtitle2">New contribution</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, mb: 2 }}>
                  <TextField label="Amount (INR)" type="number" value={contribAmount} onChange={(e) => setContribAmount(e.target.value)} size="small" required />
                  <TextField label="Date" type="date" value={contribDate} onChange={(e) => setContribDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                  <TextField label="Reference note" value={contribNote} onChange={(e) => setContribNote(e.target.value)} size="small" sx={{ flex: 1 }} placeholder="e.g. NEFT ref, tranche name" />
                  <Button variant="contained" onClick={handleAddContribution} disabled={!contribAmount}>Record</Button>
                </Stack>
                <Typography variant="subtitle2">Contribution history</Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.contributions ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No contributions yet — record the first transfer above.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {(detail.contributions ?? []).map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{formatDateForPdf(c.contributionDate)}</TableCell>
                        <TableCell align="right">{formatCurrency(c.amount)}</TableCell>
                        <TableCell>{c.referenceNote || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => c.id && handleDeleteContribution(c.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabPanel>

              <TabPanel value={detailTab} index={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Deploy captured capital when you record a stock purchase: <strong>Stock Picks → Execution</strong>.
                  Split the purchase cost across partners — that sets each partner&apos;s amount on the plan.
                </Typography>
                <Button
                  component={RouterLink}
                  to={STOCK_PICKS_PATH}
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  sx={{ mb: 2 }}
                  onClick={() => setDetailOpen(false)}
                >
                  Open Stock Picks (Execution)
                </Button>
                {(detail.planAllocations?.length ?? 0) === 0 ? (
                  <Alert severity="info" variant="outlined">
                    No plan allocations yet. After capturing capital, record a purchase on a plan and add this partner
                    to the purchase split.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2">Current plan allocations (read-only)</Typography>
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Plan</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.planAllocations!.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>{a.planCode}</TableCell>
                            <TableCell align="right">{formatCurrency(a.allocatedAmount)}</TableCell>
                            <TableCell>{a.note || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </TabPanel>

              <TabPanel value={detailTab} index={2}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Partner capital deployed to outsider lending — view linked debits, record monthly payouts to partners,
                  and track payout history. Borrower pays debit rate (typically 2%/mo), partner receives 1.5%.
                </Typography>
                <PartnerLendingVisibilityPanel
                  partnerId={detail.id}
                  partnerName={detail.name}
                  showOperatorEconomics
                  allowManage
                  onChanged={() => detail.id && getPartnerControlSnapshot(detail.id).then(setLendingSnapshot)}
                />
              </TabPanel>

              <TabPanel value={detailTab} index={3}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Partners sign in at <Link href="/partner/login" target="_blank" rel="noopener">/partner/login</Link>{' '}
                    with their phone number and a password you generate.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={detail.portalAccessEnabled ?? false}
                          onChange={(e) => handleTogglePortalAccess(e.target.checked)}
                        />
                      }
                      label="Portal access enabled"
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={detail.hasPortalPassword ? 'Password set' : 'No password yet'}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VpnKey />}
                      onClick={handleGeneratePortalPassword}
                      disabled={generatingPassword || !detail.phone?.trim()}
                    >
                      {generatingPassword ? 'Generating…' : detail.hasPortalPassword ? 'Regenerate password' : 'Generate password'}
                    </Button>
                  </Stack>
                  {!detail.phone?.trim() && (
                    <Alert severity="warning" sx={{ mt: 1.5 }} variant="outlined">
                      Add a valid phone number (edit partner) before generating a portal password.
                    </Alert>
                  )}
                </Paper>
              </TabPanel>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={statementOpen} onClose={() => !sendingStatement && setStatementOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0b1d3a 0%, #1f4068 100%)',
            color: '#f5e9c8',
            pr: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Email sx={{ color: '#c9a24a' }} />
            <Box>
              <Typography variant="overline" sx={{ color: '#c9a24a', letterSpacing: 2, lineHeight: 1 }}>
                Wealth Office
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>
                Send statement
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {statementPartner && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert
                severity="info"
                variant="outlined"
                sx={{ borderColor: '#c9a24a', '& .MuiAlert-icon': { color: '#8a6d2a' } }}
              >
                A wealth-management themed statement will be emailed to{' '}
                <strong>{statementPartner.name}</strong> summarising{' '}
                <strong>total amount invested</strong>, <strong>capital used</strong> and the
                available balance. Recipient is pre-filled from the partner profile — you can edit
                it or add more (comma / new line separated).
              </Alert>

              <TextField
                label="Recipient email(s)"
                value={statementRecipients}
                onChange={(e) => setStatementRecipients(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                required
                helperText="Comma, semicolon, space, or new-line separated. Defaults to the partner's saved email."
                autoFocus
              />

              <TextField
                label="Subject (optional)"
                value={statementSubject}
                onChange={(e) => setStatementSubject(e.target.value)}
                fullWidth
                placeholder={`Your wealth statement — ${statementPartner.name}`}
              />

              <TextField
                label="Message from your wealth manager (optional)"
                value={statementNote}
                onChange={(e) => setStatementNote(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                placeholder="A short personal note that appears as a callout near the top of the email."
                inputProps={{ maxLength: 2000 }}
              />

              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#faf6ee', borderColor: '#ecd9a4' }}>
                <Typography variant="caption" sx={{ letterSpacing: 2, color: '#8a6d2a', textTransform: 'uppercase' }}>
                  Statement preview
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Total invested</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0b1d3a' }}>
                      {formatCurrency(statementPartner.totalContributed ?? 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Capital used</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#8a6d2a' }}>
                      {formatCurrency(statementPartner.totalAllocated ?? 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Available</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {formatCurrency(statementPartner.availableBalance ?? 0)}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Contributions: {statementPartner.contributions?.length ?? 0} · Plan
                  allocations: {statementPartner.planAllocations?.length ?? 0}
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatementOpen(false)} disabled={sendingStatement}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Email />}
            onClick={handleSendStatement}
            disabled={sendingStatement || !statementRecipients.trim()}
            sx={{
              background: 'linear-gradient(135deg, #0b1d3a 0%, #1f4068 100%)',
              color: '#f5e9c8',
              '&:hover': {
                background: 'linear-gradient(135deg, #13315c 0%, #1f4068 100%)',
              },
            }}
          >
            {sendingStatement ? 'Sending…' : 'Send statement'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Partner portal password</DialogTitle>
        <DialogContent dividers>
          {generatedPassword && (
            <Stack spacing={2}>
              <Alert severity="warning">
                This password is shown <strong>only once</strong>. Copy and share it securely with{' '}
                <strong>{detail?.name}</strong>.
              </Alert>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Login phone
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  +91 {generatedPassword.loginPhone}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace' }}>
                <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                  {generatedPassword.plainPassword}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {generatedPassword.message}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={handleCopyPassword} disabled={!generatedPassword}>
            Copy password
          </Button>
          <Button variant="contained" onClick={() => setPasswordDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CapitalPartnersHub;
